/**
 * Auth Store
 *
 * Manages authentication state using Zustand.
 * Provides login, logout, status check, and initial setup functionality.
 *
 * Usage:
 *   import { useAuthStore } from '@/stores/authStore';
 *   const { user, isAuthenticated, login, logout } = useAuthStore();
 */

import { create } from 'zustand';
import { api, User, ApiRequestError } from '@/services/api';
import { useChatStore } from './chatStore';
import { useFileStore } from './fileStore';
import { useSettingsStore } from './settingsStore';

// ============================================
// Types
// ============================================

export interface AuthState {
  // State
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  needsSetup: boolean;
  error: string | null;
  initialized: boolean;

  // Actions
  checkStatus: () => Promise<void>;
  login: (username: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  handleAuthExpired: () => void;
  setup: (username: string, password: string) => Promise<boolean>;
  setClaudeConnected: (connected: boolean) => void;
  clearError: () => void;
  reset: () => void;
}

// ============================================
// Initial State
// ============================================

const initialState = {
  user: null,
  isAuthenticated: false,
  isLoading: false,
  needsSetup: false,
  error: null,
  initialized: false,
};

// User preferences are not part of authentication. A failure to load them must
// not change auth state.
function loadUserPreferences(userId: User['id']) {
  try {
    useSettingsStore.getState().loadUserTimezone(userId);
  } catch (err) {
    console.warn('Failed to load user preferences:', err);
  }
}

function resetUserScopedStores() {
  useChatStore.getState().reset();
  useFileStore.getState().reset();
  useSettingsStore.getState().resetUserSettings();
}

// ============================================
// Store
// ============================================

export const useAuthStore = create<AuthState>((set, get) => ({
  ...initialState,

  /**
   * Check current authentication status with the server.
   * Should be called on app startup to restore session state.
   */
  checkStatus: async () => {
    // Don't check if already loading
    if (get().isLoading) return;

    set({ isLoading: true, error: null });

    try {
      const status = await api.auth.getStatus();

      set({
        isAuthenticated: status.authenticated,
        user: status.user || null,
        needsSetup: status.needsSetup || false,
        isLoading: false,
        initialized: true,
      });

      if (status.authenticated && status.user) {
        loadUserPreferences(status.user.id);
      }

      // Sync displayName to settingsStore (if logged in)
      if (status.authenticated && status.user?.displayName !== undefined) {
        useSettingsStore.getState().setUserNameFromServer(status.user.displayName || '');
      }
    } catch (err) {
      // On network error or server unavailable, mark as not authenticated
      const message =
        err instanceof Error ? err.message : 'Failed to check auth status';

      set({
        isAuthenticated: false,
        user: null,
        needsSetup: false,
        isLoading: false,
        initialized: true,
        error: message,
      });
    }
  },

  /**
   * Login with username and password.
   * Returns true on success, false on failure (check error state for details).
   */
  login: async (username: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await api.auth.login(username, password);

      set({
        isAuthenticated: true,
        user: response.user,
        needsSetup: false,
        isLoading: false,
        error: null,
      });

      loadUserPreferences(response.user.id);

      // Sync displayName to settingsStore
      if (response.user?.displayName !== undefined) {
        useSettingsStore.getState().setUserNameFromServer(response.user.displayName || '');
      }

      return true;
    } catch (err) {
      let message = 'Login failed';

      if (err instanceof ApiRequestError) {
        // Use server's error message for auth errors
        message = err.message;
      } else if (err instanceof Error) {
        message = err.message;
      }

      set({
        isAuthenticated: false,
        user: null,
        isLoading: false,
        error: message,
      });

      return false;
    }
  },

  /**
   * Logout and clear session.
   * Resets all user-specific stores to prevent data leakage between users.
   */
  logout: async () => {
    set({ isLoading: true, error: null });

    try {
      await api.auth.logout();
    } catch (err) {
      // Even if logout fails server-side, clear local state
      console.error('Logout error:', err);
    }

    // Reset user-specific stores to prevent data leakage between users
    resetUserScopedStores();

    set({
      isAuthenticated: false,
      user: null,
      isLoading: false,
      error: null,
    });
  },

  /**
   * Clear local auth when the server reports that the session is no longer valid.
   */
  handleAuthExpired: () => {
    resetUserScopedStores();
    set({
      isAuthenticated: false,
      user: null,
      isLoading: false,
      needsSetup: false,
      initialized: true,
      error: 'Your session expired. Please log in again.',
    });
  },

  /**
   * Initial setup - create admin user on fresh instance.
   * Returns true on success, false on failure.
   */
  setup: async (username: string, password: string) => {
    set({ isLoading: true, error: null });

    try {
      const response = await api.auth.setup(username, password);

      // Setup auto-logs in the user
      set({
        isAuthenticated: true,
        user: response.user,
        needsSetup: false,
        isLoading: false,
        error: null,
      });
      loadUserPreferences(response.user.id);

      return true;
    } catch (err) {
      let message = 'Setup failed';

      if (err instanceof ApiRequestError) {
        message = err.message;
      } else if (err instanceof Error) {
        message = err.message;
      }

      set({
        isLoading: false,
        error: message,
      });

      return false;
    }
  },

  /**
   * Update Claude connection status without a full status check.
   * Called when user connects/disconnects Claude in settings.
   */
  setClaudeConnected: (connected: boolean) => {
    const user = get().user;
    if (user) {
      set({ user: { ...user, claudeConnected: connected } });
    }
  },

  /**
   * Clear any error message.
   */
  clearError: () => {
    set({ error: null });
  },

  /**
   * Reset store to initial state.
   * Useful for testing or complete state reset.
   */
  reset: () => {
    set(initialState);
  },
}));

// ============================================
// Selectors
// ============================================

/**
 * Select just the user from the store.
 * Useful for components that only need user info.
 */
export const selectUser = (state: AuthState) => state.user;

/**
 * Select authentication status.
 */
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated;

/**
 * Select loading state.
 */
export const selectIsLoading = (state: AuthState) => state.isLoading;

/**
 * Select whether the app needs initial setup.
 */
export const selectNeedsSetup = (state: AuthState) => state.needsSetup;

/**
 * Select any error message.
 */
export const selectError = (state: AuthState) => state.error;

/**
 * Select whether the store has been initialized (status checked).
 */
export const selectInitialized = (state: AuthState) => state.initialized;

/**
 * Select whether the current user is an admin.
 */
export const selectIsAdmin = (state: AuthState) => state.user?.isAdmin || false;

export default useAuthStore;
